import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Heart, ArrowLeft, Edit, MessageCircleHeart, Calendar, Sparkles, Share2, Copy, Check, BookOpen, FileText, LogOut, X, Plus, Trash2, ExternalLink, Video, Link2, File, ClipboardCheck, Download, MessageSquare, Eye, Target, TrendingUp, UserCheck, Star } from 'lucide-react';
import PFPGoalTouchesCard from '@/components/PFPGoalTouchesCard';
import PFPWeeklyReviewBanner from '@/components/PFPWeeklyReviewBanner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import GuidedTour from '@/components/GuidedTour';
import HandoverAnalyticsCard from '@/components/HandoverAnalyticsCard';
import ChildDiaryCard from '@/components/ChildDiaryCard';
import ProfileDriftCard from '@/components/ProfileDriftCard';
import ProgressVisualization from '@/components/ProgressVisualization';
import ParentTouchpointsCard from '@/components/ParentTouchpointsCard';
import CollapsibleSection from '@/components/CollapsibleSection';
import ChunkAnalysisDialog from '@/components/ChunkAnalysisDialog';

// Core Vocabulary Lists - Based on AAC research and Universal Core
const CORE_VOCABULARY = {
  essential: {
    label: 'Essential (Top 20)',
    description: 'Most frequently used words - start here!',
    words: ['I', 'want', 'more', 'help', 'stop', 'go', 'yes', 'no', 'that', 'like', 'not', 'do', 'it', 'get', 'make', 'put', 'see', 'can', 'you', 'my']
  },
  verbs: {
    label: 'Action Words',
    description: 'Common verbs for expressing actions',
    words: ['play', 'eat', 'drink', 'give', 'look', 'come', 'turn', 'open', 'close', 'push', 'pull', 'read', 'watch', 'listen', 'touch', 'feel', 'think', 'know', 'try', 'need']
  },
  descriptors: {
    label: 'Describing Words',
    description: 'Words for describing things and feelings',
    words: ['good', 'bad', 'big', 'little', 'hot', 'cold', 'happy', 'sad', 'mad', 'scared', 'tired', 'hungry', 'same', 'different', 'new', 'old', 'fast', 'slow', 'yucky', 'yummy']
  },
  location: {
    label: 'Location & Direction',
    description: 'Words for places and directions',
    words: ['here', 'there', 'up', 'down', 'in', 'out', 'on', 'off', 'under', 'over', 'where', 'away', 'back', 'top', 'bottom', 'inside', 'outside', 'left', 'right', 'front']
  },
  questions: {
    label: 'Question Words',
    description: 'Words for asking questions',
    words: ['what', 'who', 'where', 'when', 'why', 'how', 'which']
  },
  social: {
    label: 'Social Words',
    description: 'Words for social interaction',
    words: ['please', 'thank you', 'sorry', 'hi', 'bye', 'excuse me', 'all done', 'wait', 'again', 'mine', 'yours', 'we', 'they', 'us', 'together']
  },
  time: {
    label: 'Time Words',
    description: 'Words for expressing time',
    words: ['now', 'later', 'before', 'after', 'today', 'tomorrow', 'yesterday', 'morning', 'night', 'soon', 'always', 'never', 'sometimes', 'first', 'last']
  },
  quantity: {
    label: 'Quantity Words',
    description: 'Words for expressing amounts',
    words: ['more', 'less', 'all', 'some', 'none', 'many', 'few', 'one', 'two', 'enough', 'too much', 'a lot', 'another', 'both', 'each']
  }
};

// Core Vocabulary Selector Component
function CoreVocabularySelector({ selectedWords, onWordsChange }) {
  const [showPicker, setShowPicker] = useState(false);
  const [activeCategory, setActiveCategory] = useState('essential');
  const [customWord, setCustomWord] = useState('');

  const toggleWord = (word) => {
    const lowerWord = word.toLowerCase();
    if (selectedWords.map(w => w.toLowerCase()).includes(lowerWord)) {
      onWordsChange(selectedWords.filter(w => w.toLowerCase() !== lowerWord));
    } else {
      onWordsChange([...selectedWords, word]);
    }
  };

  const addCustomWord = () => {
    if (customWord.trim() && !selectedWords.map(w => w.toLowerCase()).includes(customWord.toLowerCase().trim())) {
      onWordsChange([...selectedWords, customWord.trim()]);
      setCustomWord('');
    }
  };

  const addAllInCategory = (category) => {
    const categoryWords = CORE_VOCABULARY[category].words;
    const newWords = categoryWords.filter(
      word => !selectedWords.map(w => w.toLowerCase()).includes(word.toLowerCase())
    );
    onWordsChange([...selectedWords, ...newWords]);
  };

  const removeWord = (word) => {
    onWordsChange(selectedWords.filter(w => w.toLowerCase() !== word.toLowerCase()));
  };

  return (
    <div className="space-y-3">
      {/* Selected words display */}
      <div className="min-h-[60px] p-3 bg-white rounded-2xl border border-stone-200">
        {selectedWords.length === 0 ? (
          <p className="text-sm text-stone-400">No core words selected yet. Click below to add words.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {selectedWords.map((word, index) => (
              <Badge 
                key={index} 
                variant="secondary" 
                className="bg-blue-100 text-blue-800 hover:bg-blue-200 cursor-pointer pr-1"
                data-testid={`selected-word-${word.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {word}
                <button 
                  onClick={() => removeWord(word)}
                  className="ml-1 p-0.5 rounded-full hover:bg-blue-300"
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* Toggle picker button */}
      <Button
        type="button"
        variant="outline"
        onClick={() => setShowPicker(!showPicker)}
        className="w-full rounded-2xl h-10"
        data-testid="open-vocabulary-picker"
      >
        <Plus className="w-4 h-4 mr-2" />
        {showPicker ? 'Hide Vocabulary List' : 'Add from Core Vocabulary'}
      </Button>

      {/* Vocabulary picker */}
      {showPicker && (
        <div className="p-4 bg-stone-50 rounded-2xl border border-stone-200 space-y-4">
          {/* Category tabs */}
          <div className="flex flex-wrap gap-2">
            {Object.entries(CORE_VOCABULARY).map(([key, cat]) => (
              <button
                key={key}
                type="button"
                onClick={() => setActiveCategory(key)}
                className={`px-3 py-1.5 text-xs rounded-full transition-colors ${
                  activeCategory === key 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-white text-stone-600 hover:bg-stone-100'
                }`}
                data-testid={`category-${key}`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Category description & add all button */}
          <div className="flex items-center justify-between">
            <p className="text-xs text-stone-500">{CORE_VOCABULARY[activeCategory].description}</p>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => addAllInCategory(activeCategory)}
              className="text-xs text-blue-600 hover:text-blue-700"
            >
              Add all
            </Button>
          </div>

          {/* Words grid */}
          <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
            {CORE_VOCABULARY[activeCategory].words.map((word) => {
              const isSelected = selectedWords.map(w => w.toLowerCase()).includes(word.toLowerCase());
              return (
                <button
                  key={word}
                  type="button"
                  onClick={() => toggleWord(word)}
                  className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                    isSelected 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-white text-stone-700 hover:bg-blue-50 border border-stone-200'
                  }`}
                  data-testid={`word-${word.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {word}
                </button>
              );
            })}
          </div>

          {/* Custom word input */}
          <div className="flex gap-2 pt-2 border-t border-stone-200">
            <Input
              type="text"
              value={customWord}
              onChange={(e) => setCustomWord(e.target.value)}
              placeholder="Add custom word..."
              className="flex-1 h-9 rounded-xl text-sm"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addCustomWord();
                }
              }}
              data-testid="custom-word-input"
            />
            <Button 
              type="button" 
              size="sm"
              onClick={addCustomWord}
              className="rounded-xl"
              data-testid="add-custom-word"
            >
              Add
            </Button>
          </div>

          {/* Selected count */}
          <p className="text-xs text-center text-stone-500">
            {selectedWords.length} word{selectedWords.length !== 1 ? 's' : ''} selected
          </p>
        </div>
      )}
    </div>
  );
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

// Common ICD-10 codes for Speech-Language Pathology
import { ICD10_CODES } from '@/data/icd10Codes';
import { GOAL_CATEGORIES, getCategoryMeta } from '@/data/goalCategories';

const getGLPStageName = (stage) => {
  const names = {
    1: 'Echolalia',
    2: 'Mitigated Gestalts',
    3: 'Single Words & Isolated Gestalts',
    4: 'First Word Combinations',
    5: 'Beginning Grammar',
    6: 'Advanced Grammar'
  };
  return names[stage] || '';
};

export default function ChildProfile({ onLogout }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [child, setChild] = useState(null);
  const [suggestingGoals, setSuggestingGoals] = useState(false);
  const [editingGoalIdx, setEditingGoalIdx] = useState(null);
  const [suggestedGoals, setSuggestedGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [showChunkAnalysis, setShowChunkAnalysis] = useState(false);
  const [formData, setFormData] = useState(null);
  const [inviteCode, setInviteCode] = useState(null);
  const [copied, setCopied] = useState(false);
  const [sharedResources, setSharedResources] = useState([]);
  const [loadingResources, setLoadingResources] = useState(false);
  // Local UI state for the "+ Add custom code" flow in the edit
  // dialog. Matches the create-form pattern in TherapistDashboard.jsx.
  const [showCustomIcd, setShowCustomIcd] = useState(false);
  const [customIcd, setCustomIcd] = useState({ code: '', label: '' });
  const user = JSON.parse(localStorage.getItem('user'));

  useEffect(() => {
    fetchChild();
    fetchSharedResources();
  }, [id]);

  const fetchChild = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/children/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChild(response.data);
      setInviteCode(response.data.invite_code);
      setFormData({
        name: response.data.name,
        age: response.data.age,
        communication_style: response.data.communication_style,
        glp_stage: response.data.glp_stage ? String(response.data.glp_stage) : 'none',
        analytic_level: response.data.analytic_level || 'none',
        uses_aac: response.data.uses_aac || false,
        aac_access_method: response.data.aac_access_method || 'none',
        core_words_focus: response.data.core_words_focus?.join(', ') || '',
        interests: response.data.interests.join(', '),
        regulation_supports: response.data.regulation_supports,
        strengths_notes: response.data.strengths_notes,
        therapy_goals: response.data.therapy_goals || [],
        parent_ids: response.data.parent_ids,
        diagnostic_codes: response.data.diagnostic_codes || [],
        pronouns: response.data.pronouns || ''
      });
    } catch (error) {
      toast.error('Failed to load child profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchSharedResources = async () => {
    setLoadingResources(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/resources/shared/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSharedResources(response.data);
    } catch (error) {
      console.error('Failed to load shared resources');
    } finally {
      setLoadingResources(false);
    }
  };

  const handleDeleteChild = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/children/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Child removed from caseload');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove child');
    }
  };

  const handleHandoverChild = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/children/${id}/handover`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(
        res.data?.handed_over_to
          ? `Handed over to ${res.data.handed_over_to}`
          : 'Child handed over',
      );
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to hand over child');
    }
  };

  const getResourceIcon = (type) => {
    switch(type) {
      case 'video_link': return <Video className="w-4 h-4" />;
      case 'external_link': return <Link2 className="w-4 h-4" />;
      case 'uploaded_file': return <File className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        age: parseInt(formData.age),
        glp_stage: formData.glp_stage && formData.glp_stage !== 'none' ? parseInt(formData.glp_stage) : null,
        analytic_level: formData.analytic_level && formData.analytic_level !== 'none' ? formData.analytic_level : null,
        uses_aac: formData.uses_aac,
        aac_access_method: formData.aac_access_method && formData.aac_access_method !== 'none' ? formData.aac_access_method : null,
        core_words_focus: formData.core_words_focus ? formData.core_words_focus.split(',').map(w => w.trim()).filter(w => w) : [],
        interests: formData.interests.split(',').map(i => i.trim()),
        therapy_goals: formData.therapy_goals || [],
        diagnostic_codes: formData.diagnostic_codes || []
      };
      await axios.put(`${API}/children/${id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Profile updated!');
      setShowEdit(false);
      fetchChild();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    }
  };

  const generateInviteCode = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/children/${id}/generate-invite-code`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInviteCode(response.data.invite_code);
      toast.success('Invite code generated!');
    } catch (error) {
      toast.error('Failed to generate invite code');
    }
  };

  // Therapist-to-therapist transfer (locum cover, caseload split).
  const [therapistShareCode, setTherapistShareCode] = useState('');
  const [therapistShareExpiresAt, setTherapistShareExpiresAt] = useState(null);
  const [generatingTherapistCode, setGeneratingTherapistCode] = useState(false);

  const generateTherapistShareCode = async () => {
    setGeneratingTherapistCode(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/children/${id}/generate-therapist-share-code`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setTherapistShareCode(res.data.code);
      setTherapistShareExpiresAt(res.data.expires_at);
      toast.success('Share code generated — send it to the other therapist');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate code');
    } finally {
      setGeneratingTherapistCode(false);
    }
  };

  const copyTherapistShareCode = () => {
    if (!therapistShareCode) return;
    navigator.clipboard.writeText(therapistShareCode);
    toast.success('Code copied');
  };

  const copyInviteCode = () => {
    if (inviteCode) {
      navigator.clipboard.writeText(inviteCode);
      setCopied(true);
      toast.success('Code copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <p className="text-stone-600">Loading...</p>
      </div>
    );
  }

  // Feb 2026 — null-guard so a bad child_id in a notification (or a
  // therapist opening a child they don't have access to) shows a
  // friendly empty state instead of crashing on `child.name`.
  if (!child) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex flex-col items-center justify-center gap-4 p-6 text-center">
        <p className="text-lg text-stone-700">This child isn't on your caseload — or the link may have expired.</p>
        <Button onClick={() => navigate('/')} className="rounded-full">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to your caseload
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="bg-white border-b border-stone-100 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              data-testid="back-btn"
              onClick={() => navigate('/')}
              className="rounded-full"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Heart className="w-5 h-5 text-primary" />
              </div>
              <h1 className="text-2xl font-serif text-primary-900">PlayFirst</h1>
            </div>
          </div>
          <Button variant="outline" data-testid="logout-btn" onClick={onLogout} className="rounded-full text-red-600 border-red-200 hover:bg-red-50">
            <LogOut className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Logout</span>
          </Button>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-start justify-between mb-8">
            <div>
              <h2 className="text-4xl md:text-5xl font-serif font-normal text-primary-900 mb-2">
                {child.name}
              </h2>
              <p className="text-lg text-stone-600">{child.age} years old</p>
              {child.is_demo && (
                <span className="inline-flex items-center gap-1 mt-1 text-xs bg-purple-100 text-purple-700 px-2.5 py-1 rounded-full font-medium">
                  <Sparkles className="w-3 h-3" /> Demo Profile — explore PlayFirst features here
                </span>
              )}
            </div>
            {user.role === 'therapist' && (
              <Dialog open={showEdit} onOpenChange={setShowEdit}>
                <DialogTrigger asChild>
                  <Button data-testid="edit-profile-btn" variant="outline" className="rounded-full">
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                </DialogTrigger>
                <DialogContent className="rounded-3xl max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-serif">Edit profile</DialogTitle>
                  </DialogHeader>
                  {formData && (
                    <form onSubmit={handleUpdate} className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label htmlFor="edit-name">Name</Label>
                        <Input
                          id="edit-name"
                          data-testid="edit-name-input"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="First name only"
                          required
                          className="h-12 rounded-2xl"
                        />
                        <p className="text-xs text-stone-400">Do not enter identifying information beyond first name.</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit-age">Age</Label>
                        <Input
                          id="edit-age"
                          data-testid="edit-age-input"
                          type="number"
                          value={formData.age}
                          onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                          required
                          className="h-12 rounded-2xl"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit-pronouns">Pronouns</Label>
                        <Select
                          value={formData.pronouns || 'unspecified'}
                          onValueChange={(value) => setFormData({ ...formData, pronouns: value === 'unspecified' ? '' : value })}
                        >
                          <SelectTrigger data-testid="edit-pronouns-select" className="h-12 rounded-2xl">
                            <SelectValue placeholder="Prefer not to say" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="unspecified">Prefer not to say</SelectItem>
                            <SelectItem value="she/her">she / her</SelectItem>
                            <SelectItem value="he/him">he / him</SelectItem>
                            <SelectItem value="they/them">they / them</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-stone-500">
                          Used across every AI-generated report, summary and session note.
                          If you correct a pronoun anywhere else, PlayFirst will update this automatically.
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit-communication">Communication style</Label>
                        <Select
                          value={formData.communication_style}
                          onValueChange={(value) => setFormData({ ...formData, communication_style: value })}
                        >
                          <SelectTrigger data-testid="edit-communication-select" className="h-12 rounded-2xl">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="GLP">GLP (Gestalt Language Processing)</SelectItem>
                            <SelectItem value="Analytic">Analytic (Traditional)</SelectItem>
                            <SelectItem value="Mixed">Mixed (Gestalt-Analytic)</SelectItem>
                            <SelectItem value="Emerging">Emerging Communicator</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {formData.communication_style === 'GLP' && (
                        <div className="space-y-2">
                          <Label htmlFor="edit-glp-stage">GLP Stage</Label>
                          <Select
                            value={formData.glp_stage}
                            onValueChange={(value) => setFormData({ ...formData, glp_stage: value })}
                          >
                            <SelectTrigger data-testid="edit-glp-stage-select" className="h-12 rounded-2xl">
                              <SelectValue placeholder="Select stage" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Not assessed yet</SelectItem>
                              <SelectItem value="1">Stage 1 - Echolalia</SelectItem>
                              <SelectItem value="2">Stage 2 - Mitigated Gestalts</SelectItem>
                              <SelectItem value="3">Stage 3 - Single Words & Isolated Gestalts</SelectItem>
                              <SelectItem value="4">Stage 4 - First Word Combinations</SelectItem>
                              <SelectItem value="5">Stage 5 - Beginning Grammar</SelectItem>
                              <SelectItem value="6">Stage 6 - Advanced Grammar</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      {formData.communication_style === 'Analytic' && (
                        <div className="space-y-2">
                          <Label htmlFor="edit-analytic-level">Language Level</Label>
                          <Select
                            value={formData.analytic_level}
                            onValueChange={(value) => setFormData({ ...formData, analytic_level: value })}
                          >
                            <SelectTrigger data-testid="edit-analytic-level-select" className="h-12 rounded-2xl">
                              <SelectValue placeholder="Select level" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Not assessed yet</SelectItem>
                              <SelectItem value="single_words">Single Words</SelectItem>
                              <SelectItem value="phrases">Phrases (2-3 words)</SelectItem>
                              <SelectItem value="sentences">Sentences</SelectItem>
                              <SelectItem value="complex">Complex Language</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      
                      {/* AAC Section */}
                      <div className="space-y-4 p-4 bg-blue-50 rounded-2xl">
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            id="uses-aac"
                            checked={formData.uses_aac}
                            onChange={(e) => setFormData({ ...formData, uses_aac: e.target.checked })}
                            className="w-5 h-5 rounded"
                            data-testid="uses-aac-checkbox"
                          />
                          <Label htmlFor="uses-aac" className="text-base">Uses AAC (Augmentative & Alternative Communication)</Label>
                        </div>
                        
                        {formData.uses_aac && (
                          <>
                            <div className="space-y-2">
                              <Label htmlFor="aac-access">Access Method</Label>
                              <Select
                                value={formData.aac_access_method}
                                onValueChange={(value) => setFormData({ ...formData, aac_access_method: value })}
                              >
                                <SelectTrigger data-testid="aac-access-select" className="h-12 rounded-2xl bg-white">
                                  <SelectValue placeholder="Select access method" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">Not specified</SelectItem>
                                  <SelectItem value="touch">Touch / Direct Select</SelectItem>
                                  <SelectItem value="eye_gaze">Eye Gaze</SelectItem>
                                  <SelectItem value="switch">Switch Scanning</SelectItem>
                                  <SelectItem value="partner_assisted">Partner Assisted</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="core-words">Core Words Focus</Label>
                              <CoreVocabularySelector 
                                selectedWords={formData.core_words_focus ? formData.core_words_focus.split(',').map(w => w.trim()).filter(w => w) : []}
                                onWordsChange={(words) => setFormData({ ...formData, core_words_focus: words.join(', ') })}
                              />
                            </div>
                          </>
                        )}
                      </div>
                      
                      {/* Diagnostic Codes Section - For EMR */}
                      <div className="space-y-4 p-4 bg-purple-50 rounded-2xl">
                        <div className="flex items-center justify-between">
                          <Label className="text-base font-medium text-purple-900">Diagnostic Codes (ICD-10)</Label>
                          <Badge variant="outline" className="text-xs text-purple-600 border-purple-300">EMR Ready</Badge>
                        </div>
                        <p className="text-xs text-purple-600">Add diagnostic codes for easy copy-paste into your EMR system.</p>
                        
                        {/* Selected codes */}
                        {formData.diagnostic_codes?.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {formData.diagnostic_codes.map((code, idx) => (
                              <Badge key={idx} className="bg-purple-100 text-purple-800 pl-3 pr-1 py-1.5 rounded-full">
                                <span className="font-mono font-medium">{code.code}</span>
                                <span className="mx-1 text-purple-500">|</span>
                                <span className="text-xs">{code.label}</span>
                                <button
                                  type="button"
                                  onClick={() => setFormData({
                                    ...formData,
                                    diagnostic_codes: formData.diagnostic_codes.filter((_, i) => i !== idx)
                                  })}
                                  className="ml-2 text-purple-500 hover:text-red-500"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </Badge>
                            ))}
                          </div>
                        )}
                        
                        {/* Code selector */}
                        <Select
                          value=""
                          onValueChange={(value) => {
                            const selectedCode = ICD10_CODES.find(c => c.code === value);
                            if (selectedCode && !formData.diagnostic_codes?.some(c => c.code === value)) {
                              setFormData({
                                ...formData,
                                diagnostic_codes: [...(formData.diagnostic_codes || []), { code: selectedCode.code, label: selectedCode.label }]
                              });
                            }
                          }}
                        >
                          <SelectTrigger className="h-12 rounded-2xl bg-white" data-testid="icd10-select">
                            <SelectValue placeholder="Add a diagnostic code..." />
                          </SelectTrigger>
                          <SelectContent className="max-h-64">
                            {Object.entries(
                              ICD10_CODES.reduce((acc, code) => {
                                if (!acc[code.category]) acc[code.category] = [];
                                acc[code.category].push(code);
                                return acc;
                              }, {})
                            ).map(([category, codes]) => (
                              <div key={category}>
                                <div className="px-2 py-1.5 text-xs font-semibold text-stone-500 bg-stone-50">{category}</div>
                                {codes.map((code) => (
                                  <SelectItem 
                                    key={code.code} 
                                    value={code.code}
                                    disabled={formData.diagnostic_codes?.some(c => c.code === code.code)}
                                  >
                                    <span className="font-mono text-purple-700">{code.code}</span>
                                    <span className="ml-2 text-stone-600">{code.label}</span>
                                  </SelectItem>
                                ))}
                              </div>
                            ))}
                          </SelectContent>
                        </Select>

                        {/* Custom code input — parity with the create form so
                            therapists can add SA-specific or DSM-style codes
                            that aren't in the standard ICD10_CODES list. */}
                        {!showCustomIcd ? (
                          <button
                            type="button"
                            onClick={() => setShowCustomIcd(true)}
                            className="text-xs text-purple-700 hover:text-purple-900 underline-offset-2 hover:underline"
                            data-testid="edit-show-custom-icd"
                          >
                            + Can&apos;t find the code? Add your own
                          </button>
                        ) : (
                          <div className="flex flex-wrap gap-2 items-stretch rounded-xl border border-purple-200 bg-purple-50/40 p-2">
                            <Input
                              placeholder="Code (e.g. F84.0)"
                              value={customIcd.code}
                              onChange={(e) => setCustomIcd({ ...customIcd, code: e.target.value })}
                              className="h-10 rounded-xl flex-1 min-w-[110px] font-mono text-sm"
                              data-testid="edit-custom-icd-code"
                              maxLength={20}
                            />
                            <Input
                              placeholder="Description"
                              value={customIcd.label}
                              onChange={(e) => setCustomIcd({ ...customIcd, label: e.target.value })}
                              className="h-10 rounded-xl flex-[2] min-w-[140px] text-sm"
                              data-testid="edit-custom-icd-label"
                              maxLength={120}
                            />
                            <Button
                              type="button"
                              size="sm"
                              onClick={() => {
                                const code = customIcd.code.trim();
                                const label = customIcd.label.trim();
                                if (!code || !label) {
                                  toast.error('Both the code and description are required');
                                  return;
                                }
                                if ((formData.diagnostic_codes || []).some((c) => c.code.toLowerCase() === code.toLowerCase())) {
                                  toast.error('That code is already added');
                                  return;
                                }
                                setFormData({
                                  ...formData,
                                  diagnostic_codes: [
                                    ...(formData.diagnostic_codes || []),
                                    { code, label, custom: true },
                                  ],
                                });
                                setCustomIcd({ code: '', label: '' });
                                setShowCustomIcd(false);
                              }}
                              className="h-10 rounded-xl"
                              data-testid="edit-custom-icd-add"
                            >
                              Add
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setCustomIcd({ code: '', label: '' });
                                setShowCustomIcd(false);
                              }}
                              className="h-10 rounded-xl text-stone-500"
                              data-testid="edit-custom-icd-cancel"
                            >
                              Cancel
                            </Button>
                          </div>
                        )}
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="edit-interests">Interests (comma-separated)</Label>
                        <Input
                          id="edit-interests"
                          data-testid="edit-interests-input"
                          value={formData.interests}
                          onChange={(e) => setFormData({ ...formData, interests: e.target.value })}
                          className="h-12 rounded-2xl"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit-regulation">Regulation/Sensory supports</Label>
                        <Textarea
                          id="edit-regulation"
                          data-testid="edit-regulation-input"
                          value={formData.regulation_supports}
                          onChange={(e) => setFormData({ ...formData, regulation_supports: e.target.value })}
                          className="rounded-2xl min-h-24"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit-strengths">Strengths notes</Label>
                        <Textarea
                          id="edit-strengths"
                          data-testid="edit-strengths-input"
                          value={formData.strengths_notes}
                          onChange={(e) => setFormData({ ...formData, strengths_notes: e.target.value })}
                          className="rounded-2xl min-h-24"
                        />
                      </div>
                      <Button type="submit" data-testid="save-changes-btn" className="w-full h-12 rounded-full">
                        Save changes
                      </Button>
                    </form>
                  )}
                </DialogContent>
              </Dialog>
            )}
          </div>

          {/* Guided Tour for Demo Child */}
          {child.is_demo && (
            <div className="mb-8">
              <GuidedTour childId={child.id} childName={child.name.replace(' (Demo)', '')} onNavigate={(path) => navigate(path)} />
            </div>
          )}

          {/* Handover analytics — surfaces a warm-start summary for children
              received via handover. Silent no-op if the child wasn't handed
              over. */}
          <HandoverAnalyticsCard childId={child.id} />

          {user.role === 'therapist' && <ChildDiaryCard childId={child.id} />}

          {/* Profile freshness — gentle nudges to keep interests, weekly
              focus, and clinical fields aligned with reality. */}
          <ProfileDriftCard
            childId={child.id}
            onAction={(target) => {
              if (target === 'edit') {
                setShowEdit(true);
              } else if (target === 'weekly-focus') {
                navigate(`/weekly-focus/${child.id}`);
              } else if (target === 'progress') {
                navigate(`/children/${child.id}?tab=progress`);
              } else if (target === 'assessments') {
                navigate(`/assessments?child=${child.id}`);
              }
            }}
          />

          {/* Child info — single combined collapsible covering the four
              "about this child" sections that previously had their own
              cards. Default-closed; persists per-child. */}
          <CollapsibleSection
            icon={<Heart className="w-5 h-5" />}
            title="Child info"
            summary="Communication style, interests, regulation & strengths"
            storageKey={`childprofile-child-info-open-${child.id}`}
            testid="child-info-collapsible"
          >
            <div className="space-y-6">
              {/* Communication style */}
              <section>
                <div className="flex items-center gap-2 mb-2">
                  <MessageCircleHeart className="w-4 h-4 text-[#7C6F9C]" />
                  <h4 className="text-base font-medium text-primary-900">Communication style</h4>
                </div>
                <p className="text-stone-700">{child.communication_style}</p>
                {child.communication_style === 'GLP' && child.glp_stage && (
                  <div className="mt-2 p-3 bg-primary/5 rounded-2xl">
                    <p className="text-sm font-medium text-primary-900">
                      Stage {child.glp_stage}: {getGLPStageName(child.glp_stage)}
                    </p>
                  </div>
                )}
                {child.communication_style === 'Analytic' && child.analytic_level && (
                  <div className="mt-2 p-3 bg-primary/5 rounded-2xl">
                    <p className="text-sm font-medium text-primary-900 capitalize">
                      Level: {child.analytic_level.replace('_', ' ')}
                    </p>
                  </div>
                )}
                {child.uses_aac && (
                  <div className="mt-2 p-3 bg-blue-50 rounded-2xl">
                    <p className="text-sm font-medium text-blue-900">
                      AAC User {child.aac_access_method && `• ${child.aac_access_method.replace('_', ' ')}`}
                    </p>
                    {child.core_words_focus?.length > 0 && (
                      <p className="text-xs text-blue-700 mt-1">
                        Core words: {child.core_words_focus.join(', ')}
                      </p>
                    )}
                  </div>
                )}
              </section>

              {/* Interests */}
              <section>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Heart className="w-4 h-4 text-[#7C6F9C]" />
                    <h4 className="text-base font-medium text-primary-900">Interests</h4>
                  </div>
                  <div className="flex items-center gap-1.5 px-2 py-0.5 bg-stone-100 rounded-full">
                    <Eye className="w-3 h-3 text-stone-500" />
                    <span className="text-[11px] text-stone-500">Visible to parents</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(child.interests || []).map((interest, i) => (
                    <span key={i} className="px-3 py-1.5 bg-secondary/10 text-secondary rounded-full text-sm">
                      {interest}
                    </span>
                  ))}
                </div>
              </section>

              {/* Regulation & sensory */}
              <section>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-[#7C6F9C]" />
                    <h4 className="text-base font-medium text-primary-900">Regulation & sensory supports</h4>
                  </div>
                  <div className="flex items-center gap-1.5 px-2 py-0.5 bg-stone-100 rounded-full">
                    <Eye className="w-3 h-3 text-stone-500" />
                    <span className="text-[11px] text-stone-500">Visible to parents</span>
                  </div>
                </div>
                <p className="text-stone-700 leading-relaxed text-sm">{child.regulation_supports}</p>
              </section>

              {/* Strengths */}
              <section>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-[#7C6F9C]" />
                    <h4 className="text-base font-medium text-primary-900">Strengths</h4>
                  </div>
                  <div className="flex items-center gap-1.5 px-2 py-0.5 bg-stone-100 rounded-full">
                    <Eye className="w-3 h-3 text-stone-500" />
                    <span className="text-[11px] text-stone-500">Visible to parents</span>
                  </div>
                </div>
                <p className="text-stone-700 leading-relaxed text-sm">{child.strengths_notes}</p>
              </section>
            </div>
          </CollapsibleSection>

          {/* Diagnostic Codes Section - For EMR */}
          {child.diagnostic_codes && child.diagnostic_codes.length > 0 && (
            <CollapsibleSection
              icon={<FileText className="w-5 h-5" />}
              title="Diagnostic Codes"
              summary={`${child.diagnostic_codes.length} code${child.diagnostic_codes.length === 1 ? '' : 's'} · for EMR copy`}
              storageKey={`childprofile-diag-codes-open-${child.id}`}
              testid="diag-codes-collapsible"
            >
              <div className="flex items-center justify-end mb-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const codesText = child.diagnostic_codes.map(c => `${c.code} - ${c.label}`).join('\n');
                    navigator.clipboard.writeText(codesText);
                    toast.success('Diagnostic codes copied to clipboard!');
                  }}
                  className="rounded-full text-purple-600 border-purple-200 hover:bg-purple-50"
                  data-testid="copy-diagnostic-codes"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy for EMR
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {child.diagnostic_codes.map((code, i) => (
                  <div
                    key={i}
                    className="px-4 py-2 bg-purple-50 text-purple-800 rounded-full flex items-center gap-2"
                  >
                    <span className="font-mono font-semibold">{code.code}</span>
                    <span className="text-purple-500">|</span>
                    <span className="text-sm">{code.label}</span>
                  </div>
                ))}
              </div>
            </CollapsibleSection>
          )}

          {/* Goals & Progress — single combined collapsible (Therapy Goals
              + Progress overview) so the profile reads more cleanly. */}
          <div id="therapy-goals-section">
          <CollapsibleSection
            icon={<Target className="w-5 h-5" />}
            title="Goals & progress"
            summary={
              child.therapy_goals && child.therapy_goals.length > 0
                ? `${child.therapy_goals.length} goal${child.therapy_goals.length === 1 ? '' : 's'} · charts & trends · visible to parents`
                : 'Add goals, view charts & trends'
            }
            storageKey={`childprofile-goals-open-${child.id}`}
            testid="goals-collapsible"
          >
            {/* Phase 3 — parent-reported goal touches. Only shown when
                therapist has starred at least one PFP priority goal AND
                at least one linked parent has logged something touching
                it. Compact by design — the therapist gets the signal
                without extra scroll. */}
            {user.role === 'therapist' && (child.pfp_priority_goals || []).length > 0 && (
              <PFPGoalTouchesCard childId={id} priorityGoals={child.pfp_priority_goals || []} />
            )}
            {/* Feb 2026 — Weekly reminder to review PFP priority goals.
                Shows for therapists when this child has a linked PFP
                parent AND the goals haven't been touched in ≥7 days. */}
            {user.role === 'therapist' && (
              <PFPWeeklyReviewBanner childId={id} />
            )}
            {/* Progress overview at the top so the therapist sees data
                first, then scrolls down to the goal list below. */}
            <div className="mb-6 pb-6 border-b border-stone-100">
              <h4 className="text-sm font-medium text-stone-500 uppercase tracking-wider mb-3">Progress overview</h4>
              <ProgressVisualization childId={child.id} />
            </div>

            <h4 className="text-sm font-medium text-stone-500 uppercase tracking-wider mb-3">Therapy goals</h4>
            <div className="flex items-center justify-end mb-3">
              <div className="flex items-center gap-1.5 px-2.5 py-1 bg-stone-100 rounded-full">
                <Eye className="w-3.5 h-3.5 text-stone-500" />
                <span className="text-xs text-stone-500">Visible to parents</span>
              </div>
            </div>
            
            {child.therapy_goals && child.therapy_goals.length > 0 ? (() => {
              // Group goals by category. Goals without a category fall to "other".
              const goalCats = child.goal_categories || {};
              const grouped = {};
              child.therapy_goals.forEach((g, idx) => {
                const cat = goalCats[g] || 'other';
                if (!grouped[cat]) grouped[cat] = [];
                grouped[cat].push({ goal: g, idx });
              });

              const updateCategory = async (goal, newCat) => {
                try {
                  const token = localStorage.getItem('token');
                  const next = { ...goalCats, [goal]: newCat };
                  const res = await axios.patch(
                    `${API}/children/${id}/goals`,
                    { therapy_goals: child.therapy_goals, goal_categories: next },
                    { headers: { Authorization: `Bearer ${token}` } },
                  );
                  setChild({ ...child, goal_categories: res.data.goal_categories });
                } catch {
                  toast.error('Could not update category');
                }
              };

              const autoCategorize = async () => {
                try {
                  const token = localStorage.getItem('token');
                  const r = await axios.post(
                    `${API}/children/${id}/auto-categorize-goals`,
                    null,
                    { headers: { Authorization: `Bearer ${token}` } },
                  );
                  setChild({ ...child, goal_categories: r.data.goal_categories });
                  toast.success(`Auto-categorized ${r.data.inferred_count} goal(s)`);
                } catch {
                  toast.error('Auto-categorize failed');
                }
              };

              // Render in the order categories are defined in GOAL_CATEGORIES
              return (
                <>
                  {user.role === 'therapist' && Object.values(goalCats).filter(Boolean).length < child.therapy_goals.length && (
                    <div className="mb-3 flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-xl">
                      <p className="text-xs text-amber-900">
                        Some goals don't have a category yet. Auto-categorize them now?
                      </p>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={autoCategorize}
                        className="text-xs rounded-full"
                        data-testid="auto-categorize-goals-btn"
                      >
                        <Sparkles className="w-3 h-3 mr-1" /> Auto-categorize
                      </Button>
                    </div>
                  )}
                  <div className="space-y-4" data-testid="grouped-goals">
                    {GOAL_CATEGORIES.filter((c) => grouped[c.slug]).map((cat) => (
                      <div key={cat.slug}>
                        <div className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border mb-2 ${cat.color}`}>
                          {cat.label}
                        </div>
                        <div className="space-y-2">
                          {grouped[cat.slug].map(({ goal, idx: i }) => (
                            <div key={i} className="flex items-start gap-3 p-3 bg-primary/5 rounded-xl">
                              <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <span className="text-xs font-medium text-primary">{i + 1}</span>
                              </div>
                              <p className="text-stone-700 flex-1">
                                {editingGoalIdx === i ? (
                                  <Input
                                    autoFocus
                                    defaultValue={goal}
                                    className="rounded-lg h-9"
                                    onKeyDown={async (e) => {
                                      if (e.key === 'Enter') {
                                        const v = e.target.value.trim();
                                        if (!v) return setEditingGoalIdx(null);
                                        try {
                                          const token = localStorage.getItem('token');
                                          const next = [...child.therapy_goals];
                                          next[i] = v;
                                          // Keep category mapping in sync if the goal text changed
                                          const remappedCats = { ...goalCats };
                                          if (goalCats[goal]) {
                                            remappedCats[v] = goalCats[goal];
                                            delete remappedCats[goal];
                                          }
                                          await axios.patch(
                                            `${API}/children/${id}/goals`,
                                            { therapy_goals: next, goal_categories: remappedCats },
                                            { headers: { Authorization: `Bearer ${token}` } },
                                          );
                                          setChild({ ...child, therapy_goals: next, goal_categories: remappedCats });
                                          toast.success('Goal updated');
                                        } catch {
                                          toast.error('Failed to update goal');
                                        }
                                        setEditingGoalIdx(null);
                                      } else if (e.key === 'Escape') {
                                        setEditingGoalIdx(null);
                                      }
                                    }}
                                    onBlur={() => setEditingGoalIdx(null)}
                                    data-testid={`goal-edit-input-${i}`}
                                  />
                                ) : (
                                  goal
                                )}
                              </p>
                              {user.role === 'therapist' && editingGoalIdx !== i && (
                                <>
                                  {/* Phase 2 — ⭐ star toggle. Up to 3 goals
                                      can be pinned as PFP priority; they get
                                      injected into the daily-idea LLM prompt
                                      so this parent's WhatsApp play ideas
                                      quietly target these focus areas. */}
                                  {(() => {
                                    const priority = child.pfp_priority_goals || [];
                                    const isStarred = priority.includes(goal);
                                    const disabled = !isStarred && priority.length >= 3;
                                    return (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className={`h-6 w-6 p-0 ${isStarred ? 'text-amber-500 hover:text-amber-600' : 'text-stone-300 hover:text-amber-400'}`}
                                        disabled={disabled}
                                        onClick={async () => {
                                          try {
                                            const token = localStorage.getItem('token');
                                            const next = isStarred
                                              ? priority.filter((g) => g !== goal)
                                              : [...priority, goal];
                                            const r = await axios.patch(
                                              `${API}/children/${id}/pfp-priority-goals`,
                                              { pfp_priority_goals: next },
                                              { headers: { Authorization: `Bearer ${token}` } },
                                            );
                                            setChild({ ...child, pfp_priority_goals: r.data.pfp_priority_goals });
                                            toast.success(
                                              isStarred
                                                ? 'Removed from PFP priority'
                                                : "⭐ Pinned — this goal now steers the parent's daily ideas",
                                            );
                                          } catch (e) {
                                            toast.error(e?.response?.data?.detail || 'Failed to update priority');
                                          }
                                        }}
                                        data-testid={`goal-star-btn-${i}`}
                                        title={
                                          isStarred
                                            ? 'Unpin from PFP priority'
                                            : disabled
                                              ? 'Max 3 priority goals — unstar one first'
                                              : "Pin to steer this parent's daily PFP ideas"
                                        }
                                      >
                                        <Star
                                          className="w-3.5 h-3.5"
                                          fill={isStarred ? 'currentColor' : 'none'}
                                        />
                                      </Button>
                                    );
                                  })()}
                                  <Select
                                    value={goalCats[goal] || 'other'}
                                    onValueChange={(v) => updateCategory(goal, v)}
                                  >
                                    <SelectTrigger
                                      className="h-6 text-[10px] w-auto px-2 border-stone-200 rounded-full"
                                      data-testid={`goal-category-select-${i}`}
                                    >
                                      <SelectValue placeholder="Set category" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {GOAL_CATEGORIES.map((c) => (
                                        <SelectItem key={c.slug} value={c.slug}>
                                          {c.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 w-6 p-0 text-stone-400 hover:text-primary"
                                    onClick={() => setEditingGoalIdx(i)}
                                    data-testid={`goal-edit-btn-${i}`}
                                    title="Edit goal"
                                  >
                                    <Edit className="w-3.5 h-3.5" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 w-6 p-0 text-stone-400 hover:text-red-500"
                                    onClick={async () => {
                                      try {
                                        const token = localStorage.getItem('token');
                                        const newGoals = child.therapy_goals.filter((_, idx) => idx !== i);
                                        const newCats = { ...goalCats };
                                        delete newCats[goal];
                                        await axios.patch(
                                          `${API}/children/${id}/goals`,
                                          { therapy_goals: newGoals, goal_categories: newCats },
                                          { headers: { Authorization: `Bearer ${token}` } },
                                        );
                                        setChild({ ...child, therapy_goals: newGoals, goal_categories: newCats });
                                        toast.success('Goal removed');
                                      } catch (error) {
                                        toast.error('Failed to remove goal');
                                      }
                                    }}
                                    data-testid={`goal-delete-btn-${i}`}
                                    title="Delete goal"
                                  >
                                    <X className="w-3.5 h-3.5" />
                                  </Button>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              );
            })() : (
              <p className="text-stone-500 italic">No therapy goals added yet. Goals from SOAP notes will appear here automatically.</p>
            )}
            
            {user.role === 'therapist' && (
              <div className="mt-4 pt-4 border-t border-stone-100">
                <div className="flex gap-2">
                  <Input
                    placeholder="Add a new goal..."
                    className="rounded-xl"
                    id="new-goal-input"
                    onKeyDown={async (e) => {
                      if (e.key === 'Enter' && e.target.value.trim()) {
                        try {
                          const token = localStorage.getItem('token');
                          const newGoals = [...(child.therapy_goals || []), e.target.value.trim()];
                          await axios.patch(`${API}/children/${id}/goals`, { therapy_goals: newGoals }, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          setChild({ ...child, therapy_goals: newGoals });
                          e.target.value = '';
                          toast.success('Goal added');
                        } catch (error) {
                          toast.error('Failed to add goal');
                        }
                      }
                    }}
                  />
                  <Button
                    variant="outline"
                    className="rounded-xl"
                    onClick={async () => {
                      const input = document.getElementById('new-goal-input');
                      if (input && input.value.trim()) {
                        try {
                          const token = localStorage.getItem('token');
                          const newGoals = [...(child.therapy_goals || []), input.value.trim()];
                          await axios.patch(`${API}/children/${id}/goals`, { therapy_goals: newGoals }, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          setChild({ ...child, therapy_goals: newGoals });
                          input.value = '';
                          toast.success('Goal added');
                        } catch (error) {
                          toast.error('Failed to add goal');
                        }
                      }
                    }}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-stone-500 mt-2">Tip: Goals from SOAP notes are automatically added here</p>
                
                {/* AI Goal Suggestions */}
                <div className="mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-full text-purple-700 border-purple-200 hover:bg-purple-50"
                    data-testid="ai-suggest-goals-btn"
                    disabled={suggestingGoals}
                    onClick={async () => {
                      setSuggestingGoals(true);
                      setSuggestedGoals([]);
                      try {
                        const token = localStorage.getItem('token');
                        const res = await axios.post(`${API}/children/${id}/suggest-goals`, {}, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        setSuggestedGoals(res.data.goals || []);
                      } catch {
                        toast.error('Failed to generate suggestions');
                      } finally {
                        setSuggestingGoals(false);
                      }
                    }}
                  >
                    {suggestingGoals ? (
                      <><Sparkles className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Generating...</>
                    ) : (
                      <><Sparkles className="w-3.5 h-3.5 mr-1.5" /> AI Suggest Goals</>
                    )}
                  </Button>

                  {suggestedGoals.length > 0 && (
                    <div className="mt-3 space-y-2 p-3 bg-purple-50 rounded-xl border border-purple-100">
                      <p className="text-xs font-medium text-purple-700">Suggested Goals (click to add)</p>
                      {suggestedGoals.map((goal, i) => (
                        <button
                          key={i}
                          className="w-full text-left p-2.5 bg-white rounded-lg border border-purple-100 text-sm text-stone-700 hover:border-purple-300 hover:bg-purple-50 transition-colors"
                          data-testid={`suggested-goal-${i}`}
                          onClick={async () => {
                            try {
                              const token = localStorage.getItem('token');
                              const newGoals = [...(child.therapy_goals || []), goal];
                              await axios.patch(`${API}/children/${id}/goals`, { therapy_goals: newGoals }, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              setChild({ ...child, therapy_goals: newGoals });
                              setSuggestedGoals(prev => prev.filter((_, idx) => idx !== i));
                              toast.success('Goal added');
                            } catch {
                              toast.error('Failed to add goal');
                            }
                          }}
                        >
                          <span className="flex items-center gap-2">
                            <Plus className="w-3.5 h-3.5 text-purple-500 shrink-0" />
                            {goal}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </CollapsibleSection>
          </div>

          {/* Share — single combined collapsible covering both
              parent and therapist sharing flows. */}
          {user.role === 'therapist' && (
            <CollapsibleSection
              icon={<Share2 className="w-5 h-5" />}
              title="Share"
              summary={
                inviteCode || therapistShareCode
                  ? `${inviteCode ? 'Parent code ready' : ''}${inviteCode && therapistShareCode ? ' · ' : ''}${therapistShareCode ? 'Therapist code ready' : ''}`
                  : 'Share with parents or another therapist'
              }
              storageKey={`childprofile-share-open-${child.id}`}
              testid="share-collapsible"
            >
              {/* Share with parents */}
              <section className="pb-6 border-b border-stone-100">
                <h4 className="text-sm font-medium text-stone-500 uppercase tracking-wider mb-3">Share with parents</h4>
                <p className="text-sm text-stone-600 mb-4">
                  Generate an invite code for parents to access this child's profile.
                </p>
                {inviteCode ? (
                  <div className="flex items-center gap-3">
                    <div className="flex-1 bg-white px-4 py-3 rounded-2xl border border-stone-200">
                      <p className="text-2xl font-mono tracking-widest text-center text-primary-900">{inviteCode}</p>
                    </div>
                    <Button
                      onClick={copyInviteCode}
                      data-testid="copy-code-btn"
                      variant="outline"
                      className="rounded-full"
                    >
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                ) : (
                  <Button
                    onClick={generateInviteCode}
                    data-testid="generate-code-btn"
                    variant="outline"
                    className="rounded-full"
                  >
                    Generate code
                  </Button>
                )}
              </section>

              {/* Share with another therapist */}
              <section className="pt-6">
                <h4 className="text-sm font-medium text-stone-500 uppercase tracking-wider mb-3">Share with another therapist</h4>
                <p className="text-sm text-stone-600 mb-4">
                  For locum cover or caseload handover. The other therapist enters this code on their PlayFirst Dashboard.
                  Code expires in 7 days or after first use. Your copy of the child stays exactly as it is — they get a copy of the profile + goals + last 10 notes + current Weekly Focus.
                </p>
                {therapistShareCode ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <div className="flex-1 bg-white px-4 py-3 rounded-2xl border border-emerald-200">
                        <p className="text-2xl font-mono tracking-widest text-center text-emerald-900" data-testid="therapist-share-code-display">
                          {therapistShareCode}
                        </p>
                      </div>
                      <Button
                        onClick={copyTherapistShareCode}
                        variant="outline"
                        className="rounded-full"
                        data-testid="therapist-share-code-copy-btn"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                    {therapistShareExpiresAt && (
                      <p className="text-xs text-stone-500">
                        Expires {new Date(therapistShareExpiresAt).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                ) : (
                  <Button
                    onClick={generateTherapistShareCode}
                    disabled={generatingTherapistCode}
                    variant="outline"
                    className="rounded-full"
                    data-testid="therapist-share-generate-btn"
                  >
                    {generatingTherapistCode ? 'Generating…' : 'Generate share code'}
                  </Button>
                )}
              </section>

              {/* Feb 2026 — Connect a PlayFirst Parent (PFP) account */}
              <section className="pt-6 border-t border-stone-100 mt-6">
                <h4 className="text-sm font-medium text-stone-500 uppercase tracking-wider mb-3">
                  Connect a PlayFirst Parent (PFP) account
                </h4>
                <p className="text-sm text-stone-600 mb-2">
                  Invite this child's parent to PlayFirst Parent — a WhatsApp-based daily play idea companion.
                  When they accept, this profile links to their PFP child.
                </p>
                <p className="text-sm text-stone-600 mb-4">
                  <strong>To shape those daily ideas:</strong> star up to <strong>3 goals</strong> in the
                  {' '}
                  <a
                    href="#therapy-goals-section"
                    onClick={(e) => {
                      e.preventDefault();
                      const el = document.getElementById('therapy-goals-section');
                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                    className="text-[#7C6F9C] underline underline-offset-2 font-medium"
                    data-testid="pfp-goals-jumpto-link"
                  >
                    Therapy Goals section above
                  </a>
                  . Those become the PFP focus and gently steer the family's daily play idea on WhatsApp.
                </p>
                <ConnectPfpParentBlock childId={child.id} />
                <ConnectedPfpParentsList childId={child.id} />
              </section>
            </CollapsibleSection>
          )}


          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link to={`/weekly-focus/${child.id}`}>
              <Card className="p-6 bg-gradient-to-br from-primary/5 to-primary/10 rounded-3xl border border-primary/20 hover:shadow-md transition-all cursor-pointer" data-testid="weekly-focus-card">
                <Calendar className="w-8 h-8 text-primary mb-3" />
                <h4 className="text-lg font-medium text-primary-900">Weekly focus</h4>
              </Card>
            </Link>

            <div className="relative">
              <Link to={`/chunks/${child.id}`}>
                <Card className="p-6 bg-gradient-to-br from-secondary/5 to-secondary/10 rounded-3xl border border-secondary/20 hover:shadow-md transition-all cursor-pointer" data-testid="chunk-bank-card">
                  <MessageCircleHeart className="w-8 h-8 text-secondary mb-3" />
                  <h4 className="text-lg font-medium text-primary-900">Chunk bank</h4>
                </Card>
              </Link>
              {user.role === 'therapist' && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowChunkAnalysis(true);
                  }}
                  className="absolute top-3 right-3 p-2 rounded-full bg-white/80 hover:bg-white shadow-sm border border-stone-200 text-primary"
                  title="Analyze chunk bank"
                  data-testid="analyze-chunks-profile-btn"
                >
                  <Sparkles className="w-4 h-4" />
                </button>
              )}
            </div>

            {user.role === 'parent' && (
              <Link to={`/parent/feedback/${child.id}`}>
                <Card className="p-6 bg-gradient-to-br from-green-50 to-emerald-100 rounded-3xl border border-green-200 hover:shadow-md transition-all cursor-pointer" data-testid="therapy-feedback-card">
                  <MessageSquare className="w-8 h-8 text-green-700 mb-3" />
                  <h4 className="text-lg font-medium text-primary-900">Therapy Feedback</h4>
                  <p className="text-xs text-stone-600 mt-1">Session notes & updates</p>
                </Card>
              </Link>
            )}

            {user.role === 'therapist' && (
              <Link to={`/activities?childId=${child.id}`}>
                <Card className="p-6 bg-gradient-to-br from-stone-100 to-stone-200 rounded-3xl border border-stone-300 hover:shadow-md transition-all cursor-pointer" data-testid="activities-card">
                  <Sparkles className="w-8 h-8 text-stone-700 mb-3" />
                  <h4 className="text-lg font-medium text-primary-900">Activities</h4>
                  {child.glp_stage && (
                    <p className="text-xs text-stone-600 mt-1">Adapted for Stage {child.glp_stage}</p>
                  )}
                </Card>
              </Link>
            )}
            
            {user.role === 'therapist' && (
              <Link to={`/assessments/${child.id}`}>
                <Card className="p-6 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-3xl border border-blue-200 hover:shadow-md transition-all cursor-pointer" data-testid="assessments-card">
                  <ClipboardCheck className="w-8 h-8 text-blue-700 mb-3" />
                  <h4 className="text-lg font-medium text-primary-900">Assessments & Reports</h4>
                  <p className="text-xs text-stone-600 mt-1">Evaluations, progress reports & AI insights</p>
                </Card>
              </Link>
            )}
            
            {user.role === 'therapist' && (
              <Link to={`/session-notes/${child.id}`}>
                <Card className="p-6 bg-gradient-to-br from-amber-50 to-orange-100 rounded-3xl border border-amber-200 hover:shadow-md transition-all cursor-pointer" data-testid="session-notes-card">
                  <FileText className="w-8 h-8 text-amber-700 mb-3" />
                  <h4 className="text-lg font-medium text-primary-900">Session Notes</h4>
                  <p className="text-xs text-stone-600 mt-1">SOAP notes & documentation</p>
                </Card>
              </Link>
            )}
            
            {user.role === 'therapist' && (
              <Link to={`/parent-progress/${child.id}`}>
                <Card className="p-6 bg-gradient-to-br from-teal-50 to-cyan-100 rounded-3xl border border-teal-200 hover:shadow-md transition-all cursor-pointer" data-testid="parent-progress-card">
                  <Check className="w-8 h-8 text-teal-700 mb-3" />
                  <h4 className="text-lg font-medium text-primary-900">Parent Progress</h4>
                  <p className="text-xs text-stone-600 mt-1">Activity completions & comments</p>
                </Card>
              </Link>
            )}
          </div>

          {/* Parent Touchpoints — Multi-SOAP summary + General feedback messages */}
          <CollapsibleSection
            icon={<MessageSquare className="w-5 h-5" />}
            title="Parent Touchpoints"
            summary="Multi-SOAP AI recap, parent feedback messages, mark-as-read"
            storageKey={`childprofile-touchpoints-open-${child.id}`}
            testid="touchpoints-collapsible"
          >
            <ParentTouchpointsCard child={child} embedded />
          </CollapsibleSection>

          {/* Resource Library - Shared resources for this child's family */}
          <CollapsibleSection
            icon={<BookOpen className="w-5 h-5" />}
            title="Resource Library"
            summary={
              sharedResources.length > 0
                ? `${sharedResources.length} resource${sharedResources.length === 1 ? '' : 's'} shared`
                : (user.role === 'therapist' ? 'No resources shared yet' : 'No resources shared yet')
            }
            storageKey={`childprofile-resources-open-${child.id}`}
            testid="resources-collapsible"
          >
            <div data-testid="resource-library-section">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-stone-500">
                  {user.role === 'therapist'
                    ? `Resources shared with ${child.name}'s family`
                    : 'Resources your therapist has shared with you'
                  }
                </p>
                {user.role === 'therapist' && (
                  <Link to="/learn">
                    <Button variant="outline" size="sm" className="rounded-full">
                      <Plus className="w-4 h-4 mr-1" />
                      Share Resource
                    </Button>
                  </Link>
                )}
              </div>

            {loadingResources ? (
              <p className="text-stone-500 text-center py-4">Loading...</p>
            ) : sharedResources.length === 0 ? (
              <div className="text-center py-6 bg-stone-50 rounded-2xl">
                <BookOpen className="w-10 h-10 text-stone-300 mx-auto mb-2" />
                <p className="text-stone-500">No resources shared yet</p>
                {user.role === 'therapist' ? (
                  <>
                    <p className="text-xs text-stone-400 mt-1">Share educational materials from the Learn More section</p>
                    <Link to="/learn">
                      <Button variant="outline" size="sm" className="mt-3 rounded-full">
                        Browse Resources
                      </Button>
                    </Link>
                  </>
                ) : (
                  <p className="text-xs text-stone-400 mt-1">Your therapist will share helpful resources here</p>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {sharedResources.map((share) => {
                  const isParent = user.role === 'parent';
                  const isUnread = isParent && !share.read_at;
                  return (
                  <div
                    key={share.id}
                    className={`flex items-center justify-between p-3 rounded-xl ${
                      isUnread ? 'bg-amber-50 border border-amber-200' : 'bg-stone-50'
                    }`}
                    data-testid={`shared-resource-row-${share.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                        {getResourceIcon(share.resource?.resource_type)}
                      </div>
                      <div>
                        <p className="font-medium text-stone-800 text-sm">{share.resource?.title || 'Untitled Resource'}</p>
                        <p className="text-xs text-stone-500">
                          Shared {share.shared_at ? new Date(share.shared_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'recently'}
                          {share.therapist_note && ` • "${share.therapist_note}"`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {share.resource?.external_url && (
                        <a href={share.resource.external_url} target="_blank" rel="noopener noreferrer">
                          <Button variant="ghost" size="sm" className="rounded-full" data-testid={`open-resource-${share.id}`}>
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        </a>
                      )}
                      {share.resource?.resource_type === 'uploaded_file' && share.resource?.file_name && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="rounded-full"
                          data-testid={`download-resource-${share.id}`}
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            try {
                              const token = localStorage.getItem('token');
                              const response = await axios.get(`${API}/resources/${share.resource.id}/download`, {
                                headers: { Authorization: `Bearer ${token}` },
                                responseType: 'blob'
                              });
                              
                              // Create blob with proper MIME type
                              const contentType = response.headers['content-type'] || 'application/octet-stream';
                              const blob = new Blob([response.data], { type: contentType });
                              const url = window.URL.createObjectURL(blob);
                              
                              // For iOS Safari - use different approach
                              const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
                              if (isIOS) {
                                // On iOS, open in new tab which will show preview/download option
                                window.open(url, '_blank');
                                toast.success('File opened - tap "Open in..." to save');
                              } else {
                                // Standard download for other browsers
                                const link = document.createElement('a');
                                link.href = url;
                                link.setAttribute('download', share.resource.file_name);
                                document.body.appendChild(link);
                                link.click();
                                link.remove();
                                toast.success('Download started');
                              }
                              
                              // Cleanup after delay
                              setTimeout(() => window.URL.revokeObjectURL(url), 10000);
                            } catch (error) {
                              console.error('Download error:', error);
                              toast.error('Failed to download file');
                            }
                          }}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      )}
                      {isParent && (
                        isUnread ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-full text-amber-700 border-amber-300 hover:bg-amber-100"
                            data-testid={`mark-read-resource-${share.id}`}
                            onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              try {
                                const token = localStorage.getItem('token');
                                await axios.post(
                                  `${API}/resources/share/${share.id}/mark-read`,
                                  {},
                                  { headers: { Authorization: `Bearer ${token}` } },
                                );
                                toast.success('Marked as read');
                                fetchSharedResources();
                              } catch (err) {
                                toast.error('Failed to mark as read');
                              }
                            }}
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Mark read
                          </Button>
                        ) : (
                          <span
                            className="text-xs text-emerald-600 flex items-center gap-1 px-2"
                            data-testid={`resource-read-badge-${share.id}`}
                          >
                            <Check className="w-3 h-3" />
                            Read
                          </span>
                        )
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
            </div>
          </CollapsibleSection>

          {/* Remove from Caseload - Only for therapists */}
          {user.role === 'therapist' && (
            <CollapsibleSection
              icon={<Trash2 className="w-5 h-5" />}
              title="Remove from caseload"
              summary={`Tap to remove ${child.name} from your caseload`}
              storageKey={`childprofile-danger-open-${child.id}`}
              testid="danger-zone"
              className="!border-red-100"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-stone-500">This will remove {child.name} from your caseload. This action cannot be undone.</p>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" className="rounded-full text-emerald-700 border-emerald-200 hover:bg-emerald-50 mr-2" data-testid="handover-child-btn">
                      <UserCheck className="w-4 h-4 mr-2" />
                      Hand over to another therapist
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Hand {child.name} over?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This removes {child.name} from <strong>your caseload</strong> without deleting them from PlayFirst.
                        The receiving therapist keeps their copy and continues their care.
                        <br/><br/>
                        <strong>Before you continue:</strong> the receiving therapist must have already redeemed
                        a share code for this child. If they haven't, generate one and let them redeem it first.
                        <br/><br/>
                        This action cannot be undone from your side — you'd need to be re-invited.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleHandoverChild}
                        className="rounded-full bg-emerald-600 hover:bg-emerald-700"
                        data-testid="confirm-handover-btn"
                      >
                        Yes, hand over
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" className="rounded-full text-red-600 border-red-200 hover:bg-red-50" data-testid="remove-child-btn">
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete from PlayFirst
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete {child.name} from PlayFirst?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This permanently removes this child's profile — chunks, progress notes, weekly focus and any linked assessments.
                        Parents connected to this child will also lose access. This action cannot be undone.
                        <br/><br/>
                        If you're handing over care to another therapist, use <em>Hand over</em> instead so their profile continues on PlayFirst.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
                      <AlertDialogAction 
                        onClick={handleDeleteChild}
                        className="rounded-full bg-red-600 hover:bg-red-700"
                        data-testid="confirm-remove-btn"
                      >
                        Yes, permanently delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </CollapsibleSection>
          )}
        </motion.div>
      </main>

      <ChunkAnalysisDialog
        open={showChunkAnalysis}
        onOpenChange={setShowChunkAnalysis}
        childId={child?.id}
      />
    </div>
  );
}


/* ==================================================================
 * ConnectPfpParentBlock
 * -------------------------------------------------------------------
 * Small self-contained UI on ChildProfile that lets a Clinical
 * therapist invite the parent to PlayFirst Parent (PFP) directly from
 * a child's profile. On duplicate we surface an "already sent —
 * resend?" dialog instead of a hard error.
 * ==================================================================*/
function ConnectPfpParentBlock({ childId }) {
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [lastInvite, setLastInvite] = useState(null);
  const [resendDialog, setResendDialog] = useState(null); // {link_id, email}
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const sendInvite = async (isResend = false) => {
    if (!email.trim()) return toast.error("Enter the parent's email");
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API}/therapist/parent-links`,
        { parent_email: email.trim().toLowerCase(), clinical_child_id: childId },
        auth(),
      );
      setLastInvite(data);
      setEmail('');
      toast.success(
        data.auto_provisioned
          ? 'Invited — parent will get a magic-link email to set their password.'
          : 'Invite request sent — parent will see it in their PFP inbox.',
      );
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || 'Could not send invite';
      if (status === 409) {
        // Look up the existing link so we can resend it (or at least surface the option).
        try {
          const { data } = await axios.get(`${API}/therapist/parent-links`, auth());
          const existing = (data.links || []).find(
            (l) => (l.parent_email || '').toLowerCase() === email.trim().toLowerCase()
                && l.status !== 'revoked'
          );
          setResendDialog({
            link_id: existing?.id,
            email: email.trim(),
            created_at: existing?.created_at,
            status: existing?.status,
          });
        } catch {
          toast.warning(detail);
        }
      } else {
        toast.error(detail);
      }
    } finally {
      setSending(false);
    }
  };

  const doResend = async () => {
    if (!resendDialog?.link_id) {
      setResendDialog(null);
      return;
    }
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API}/therapist/parent-links/${resendDialog.link_id}/resend`, {}, auth()
      );
      if (data.email_sent) {
        toast.success(`Re-sent invite email to ${resendDialog.email}`);
      } else if (data.parent_auth_provider && data.parent_auth_provider !== 'invite_pending') {
        toast.info(`${resendDialog.email} already has a PlayFirst account — ask them to sign in.`);
      } else {
        toast.warning('Invite recorded but email delivery failed. Check spam or try later.');
      }
      setEmail('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not resend');
    } finally {
      setSending(false);
      setResendDialog(null);
    }
  };

  return (
    <>
      <div className="flex flex-col sm:flex-row gap-2" data-testid="connect-pfp-parent-block">
        <Input
          type="email"
          placeholder="parent@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="rounded-2xl h-11 flex-1"
          data-testid="connect-pfp-parent-email-input"
        />
        <Button
          onClick={() => sendInvite(false)}
          disabled={sending || !email.trim()}
          className="rounded-full bg-[#7C6F9C] hover:bg-[#6a5f8a] whitespace-nowrap"
          data-testid="connect-pfp-parent-invite-btn"
        >
          {sending ? 'Sending…' : 'Invite to PFP'}
        </Button>
      </div>
      {lastInvite && (
        <p className="text-xs text-stone-500 mt-3" data-testid="connect-pfp-parent-last">
          Invited <strong>{lastInvite.parent_email}</strong> — status <em>{lastInvite.status}</em>
        </p>
      )}

      <AlertDialog open={!!resendDialog} onOpenChange={(v) => !v && setResendDialog(null)}>
        <AlertDialogContent data-testid="resend-invite-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Invite already sent</AlertDialogTitle>
            <AlertDialogDescription>
              {resendDialog?.created_at
                ? `An invite was sent to ${resendDialog.email} on ${new Date(resendDialog.created_at).toLocaleDateString()}.`
                : `An invite to ${resendDialog?.email} already exists.`}
              {' '}Send it again anyway? Useful if the parent didn't receive the first email (spam / typo).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="resend-invite-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doResend}
              disabled={sending}
              className="bg-[#7C6F9C] hover:bg-[#6a5f8a]"
              data-testid="resend-invite-confirm"
            >
              {sending ? 'Sending…' : 'Send again'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}


/* ==================================================================
 * ConnectedPfpParentsList
 * -------------------------------------------------------------------
 * Compact status strip that lists PFP parents currently linked to
 * this clinical child (from `therapist_parent_links`, filtered by
 * `clinical_child_id`). Shows pending vs active vs auto_therapist_lite
 * so the therapist can see at a glance whether an invite has landed.
 * ==================================================================*/
function ConnectedPfpParentsList({ childId }) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/therapist/parent-links`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        const forThisChild = (data.links || []).filter(
          (l) => l.clinical_child_id === childId && l.status !== 'revoked' && l.status !== 'declined',
        );
        setLinks(forThisChild);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, [childId, API]);

  if (loading) return null;
  if (!links.length) return null;

  return (
    <div className="mt-4 space-y-2" data-testid="connected-pfp-parents-list">
      <p className="text-xs uppercase tracking-wider text-stone-500 font-medium">
        Linked PFP parents
      </p>
      {links.map((l) => {
        const isActive = l.status === 'active';
        const isPending = l.status === 'pending';
        return (
          <div
            key={l.id}
            className={`flex items-center justify-between rounded-2xl border p-3 ${
              isActive
                ? 'bg-emerald-50/60 border-emerald-200'
                : 'bg-amber-50/50 border-amber-200'
            }`}
            data-testid={`connected-pfp-parent-${l.id}`}
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-stone-800 truncate">
                  {l.parent_name || l.parent_email}
                </span>
                {isActive && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200">
                    Connected
                  </span>
                )}
                {isPending && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                    Awaiting parent
                  </span>
                )}
              </div>
              <p className="text-xs text-stone-500 mt-0.5">
                {isActive
                  ? `Their PFP daily play ideas now pick up any goals you star for ${l.child_name || 'this child'}.`
                  : 'Waiting for them to accept the invite email.'}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
